import { StyleSheet, css } from 'aphrodite'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import BlurbSection from '../components/BlurbSection'
import HeroSection from '../components/HeroSection'
import LandingSection from '../components/LandingSection'
import TitleBlurbSection from '../components/TitleBlurbSection'
import LandingFooter from '../components/navigation/LandingFooter'
import LandingHeader from '../components/navigation/LandingHeader'

export default function NewsAndPressPage() {
  const navigate = useNavigate()

  const handleJoinCommunityClick = () => {
    window.scrollTo(0, 0)
    navigate('/register')
  }

  return (
    <div className={css(styles.container)}>
      <LandingHeader />
      <HeroSection
        subtitle="News & Press"
        title="STAYING CONNECTED WITH THE COMMUNITY"
        description="Stay up-to-date with the latest developments, announcements, and press coverage from the DayDaCo Ecosystem. From product updates to industry insights, we're committed to keeping our community informed and engaged."
        imageSrc="/media.jpg"
        imageAlt="News and Press updates"
      />

      {/* Press Contact Section */}
      <section className={css(styles.pressContactSection)}>
        <div className={css(styles.pressContactContent)}>
          <h2 className={css(styles.pressContactTitle)}>CONTACT US</h2>
          <div className={css(styles.contactInfo)}>
            <a
              href="mailto:press@daydaco.com"
              className={css(styles.emailLink)}
            >
              press@daydaco.com
            </a>
          </div>
          <div className={css(styles.pressDescription)}>
            <p className={css(styles.pressHeader)}>
              Our press team loves working with journalists to share compelling,
              unique stories!
            </p>
            <p className={css(styles.pressText)}>
              If you are a member of the media and would like to talk, please
              get in touch with us by sending an email to{' '}
              <a
                href="mailto:press@daydaco.com"
                className={css(styles.inlineEmailLink)}
              >
                press@daydaco.com
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Fact Sheet Section */}
      <section className={css(styles.factSheetSection)}>
        <div className={css(styles.factSheetContent)}>
          <h2 className={css(styles.sectionTitle)}>
            READ DAYDACO'S FACT SHEET
          </h2>
          <p className={css(styles.factSheetDescription)}>
            DayDaCo's Fact Sheet - Who we are and what we're offering
          </p>
          <button className={css(styles.factSheetButton)}>
            <i className="fas fa-file-pdf" style={{ marginRight: '8px' }} />
            Download Fact Sheet (PDF)
          </button>
        </div>
      </section>

      {/* What's New Section */}
      <section className={css(styles.newsSection)}>
        <div className={css(styles.newsContent)}>
          <h2 className={css(styles.newsTitle)}>WHAT'S NEW AT DAYDACO</h2>
          <div className={css(styles.placeholderContent)}>
            <p className={css(styles.placeholderText)}>
              We are still in the process of creating content, so please come
              back and visit us to see what is new at DayDaCo!
            </p>
          </div>
        </div>
      </section>

      {/* Press Releases Section */}
      <section className={css(styles.pressReleasesSection)}>
        <div className={css(styles.pressReleasesContent)}>
          <h2 className={css(styles.pressReleasesTitle)}>PRESS RELEASES</h2>
          <div className={css(styles.placeholderContent)}>
            <p className={css(styles.placeholderText)}>
              We are still in the process of creating content, so please come
              back and visit us to see what is new at DayDaCo!
            </p>
          </div>
        </div>
      </section>

      <LandingSection
        title="JOIN THE ECOSYSTEM"
        description="Work smarter with real-time updates, instant data access, and modular, digital templates and workflows. Cut down on paperwork, build trust, and reduce unnecessary driving – all in one Ecosystem."
        imageSrc="/ecosystem2.webp"
        imageAlt="Construction Team"
        buttonText="Join the Ecosystem"
        testimonialText="Trusted by 10,000+ operators,"
        userCount="contractors and haulers"
        onButtonClick={handleJoinCommunityClick}
      />

      <LandingFooter />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  pressContactSection: {
    padding: '40px 1rem',
    backgroundColor: '#F2F5F7',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  pressContactContent: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pressContactTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  contactInfo: {
    backgroundColor: '#ffffff',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
  },
  emailLink: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1F407E',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
    '@media (min-width: 768px)': {
      fontSize: '24px',
    },
  },
  pressDescription: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  pressHeader: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#1F3D61',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
  pressText: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#4B5563',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
  inlineEmailLink: {
    color: '#1F407E',
    textDecoration: 'none',
    fontWeight: 600,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  factSheetSection: {
    padding: '40px 1rem',
    backgroundColor: '#ffffff',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
  },
  factSheetContent: {
    maxWidth: '600px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'center',
  },
  sectionTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '28px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '36px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '48px',
    },
  },
  factSheetDescription: {
    fontSize: '16px',
    color: '#4B5563',
    lineHeight: 1.5,
    '@media (min-width: 768px)': {
      fontSize: '18px',
    },
  },
  factSheetButton: {
    backgroundColor: '#6DC3FF',
    color: '#1F407E',
    padding: '12px 32px',
    borderRadius: '24px',
    fontSize: '16px',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      backgroundColor: '#5AB4F0',
    },
  },
  newsSection: {
    padding: '40px 1rem',
    backgroundColor: '#F2F5F7',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  newsContent: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  newsTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  pressReleasesSection: {
    padding: '40px 1rem',
    backgroundColor: '#ffffff',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  pressReleasesContent: {
    maxWidth: '800px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  pressReleasesTitle: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#1F3D61',
    lineHeight: 1.2,
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  placeholderContent: {
    backgroundColor: '#F8F9FA',
    padding: '24px',
    borderRadius: '12px',
    border: '2px dashed #D1D5DB',
  },
  placeholderText: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: '#6B7280',
    fontStyle: 'italic',
    margin: 0,
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
  },
})
